import { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Settings, Lock, Hourglass, Ban, Power, ChevronDown, Bot, Camera } from "lucide-react";

/**
 * Phase 3.2 — host/cohost Manage dropdown.
 *
 * Phase A R4 B3: End-room MenuItem removed; the destructive action lives
 * on the bottom dock as a single contextual button (data-testid
 * `room-leave-or-end`, data-role="host").
 *
 * Phase A R4 B4: dropdown menu auto-positions to stay inside the viewport
 * — flips upward when there's no room below, clamps max-height so options
 * scroll internally, and never crosses the right edge.
 *
 * Phase A R6 B5: dropdown is now PORTALED to document.body. The previous
 * version used `position: fixed` inside a normal child div, but ANY
 * ancestor with `transform`, `filter`, `backdrop-filter`, `perspective`,
 * `will-change` (etc.) becomes the containing block for fixed children
 * — and several of this app's bottom-dock / glass panels use
 * `backdrop-filter: blur(...)`. That collapsed the dropdown to inside an
 * `overflow-hidden`/clipped region and the user saw "no options".
 * Portaling to body + bumping z-index past every glass overlay fixes it.
 * Also: testids renamed to match Round 6 spec
 * (host-manage-begin-closing, host-manage-stop-all-activities).
 */
export default function HostManageDropdown({
  locked, onToggleLock,
  onBeginClosing,
  onStopAllActivities,
  onResetBans,
  onOpenBotsAndRules,
  alwaysCameraOn,
  onToggleAlwaysCamera,
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: 320, openUp: false });

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      // Allow clicks inside the trigger OR the portaled menu.
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onEsc);
    return () => { window.removeEventListener("mousedown", onClick); window.removeEventListener("keydown", onEsc); };
  }, [open]);

  // Clamp the portaled menu inside the viewport at any zoom level.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;
    const recalc = () => {
      const tr = trigger.getBoundingClientRect();
      const margin = 8;
      const viewW = window.innerWidth;
      const viewH = window.innerHeight;
      const desiredW = Math.min(menu.offsetWidth || 224, viewW - margin * 2);
      // Horizontal: right-align under trigger, never cross right edge.
      let left = tr.right - desiredW;
      if (left < margin) left = margin;
      if (left + desiredW > viewW - margin) left = viewW - margin - desiredW;
      // Vertical: open below if space, else above. Always clamp inside viewport.
      const spaceBelow = viewH - tr.bottom - margin;
      const spaceAbove = tr.top - margin;
      const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
      const top = openUp ? Math.max(margin, tr.top - menu.offsetHeight - 4) : tr.bottom + 4;
      const maxHeight = Math.max(120, openUp ? spaceAbove : spaceBelow);
      setPos({ top, left, maxHeight, openUp });
    };
    recalc();
    window.addEventListener("resize", recalc);
    window.addEventListener("scroll", recalc, true);
    return () => {
      window.removeEventListener("resize", recalc);
      window.removeEventListener("scroll", recalc, true);
    };
  }, [open]);

  // The portaled menu — rendered into document.body so it escapes any
  // ancestor with transform / filter / backdrop-filter / overflow:hidden.
  const menu = open ? ReactDOM.createPortal(
    <div ref={menuRef}
         style={{ position: "fixed", top: pos.top, left: pos.left, width: 224, maxHeight: pos.maxHeight, overflowY: "auto", zIndex: 10000 }}
         className="bg-[#16161d] border border-white/10 rounded-xl shadow-2xl py-1"
         data-testid="host-manage-menu"
         data-open-up={pos.openUp ? "1" : "0"}>
      <MenuItem icon={Lock} label={locked ? "Reopen room" : "Lock room"}
                onClick={() => { setOpen(false); onToggleLock?.(); }}
                testid="host-manage-lock"
                accent={locked ? "amber" : null} />
      {typeof alwaysCameraOn === "boolean" && onToggleAlwaysCamera && (
        <ToggleItem
          icon={Camera}
          label="Always start cameras"
          checked={alwaysCameraOn}
          onClick={() => onToggleAlwaysCamera?.(!alwaysCameraOn)}
          testid="host-manage-always-camera-toggle"
        />
      )}
      {onOpenBotsAndRules && (
        <MenuItem icon={Bot} label="Bots & Rules"
                  onClick={() => { setOpen(false); onOpenBotsAndRules?.(); }}
                  testid="host-manage-bots-and-rules" />
      )}
      <MenuItem icon={Hourglass} label="Begin closing (60s)"
                onClick={() => { setOpen(false); onBeginClosing?.(); }}
                testid="host-manage-begin-closing" />
      <MenuItem icon={Ban} label="Reset bans"
                onClick={() => { setOpen(false); onResetBans?.(); }}
                testid="host-manage-reset-bans" />
      <MenuItem icon={Power} label="Stop all activities"
                onClick={() => { setOpen(false); onStopAllActivities?.(); }}
                testid="host-manage-stop-all-activities" />
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative">
      <button ref={triggerRef} onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-semibold transition-colors"
              data-testid="host-manage-btn" title="Host controls">
        <Settings className="w-3.5 h-3.5 text-[#00E0FF]" />
        Manage
        <ChevronDown className={`w-3 h-3 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {menu}
    </div>
  );
}

function ToggleItem({ icon: Icon, label, checked, onClick, testid }) {
  return (
    <button onClick={onClick}
            className="w-full px-3 py-2 text-left text-[12px] flex items-center gap-2 text-white/85 hover:bg-white/5"
            data-testid={testid}
            role="switch"
            aria-checked={checked}>
      <Icon className="w-3.5 h-3.5" />
      <span className="flex-1">{label}</span>
      <span className={`w-8 h-4 rounded-full relative transition-colors ${checked ? "bg-gradient-to-r from-[#FF007F] to-[#00F0FF]" : "bg-white/15"}`}>
        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
    </button>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, destructive, muted, accent }) {
  let cls = "text-white/85 hover:bg-white/5";
  if (destructive) cls = "text-rose-300 hover:bg-rose-500/10";
  else if (muted) cls = "text-white/45 hover:bg-white/5";
  else if (accent === "amber") cls = "text-amber-300 hover:bg-amber-500/10";
  return (
    <button onClick={onClick}
            className={`w-full px-3 py-2 text-left text-[12px] flex items-center gap-2 ${cls}`}
            data-testid={testid}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
